import React, { useState } from "react";
import { Flame, X, TrendingUp, Loader2 } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { api, auraFmt, auraExact, multX } from "@/lib/api";
import { cn } from "@/lib/utils";

// Tappable Aura score. On click opens the Aura Breakdown. For the current user it
// fetches the full ledger breakdown; for others it shows total/today/streak.
export function AuraBadge({ profile, self = false, size = "lg" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const total = profile.aura_total_centi || 0;
  const today = profile.aura_today_centi || 0;
  const streak = profile.aura_streak_days || 0;
  const mult = profile.aura_multiplier_bps || 10000;

  const openPanel = async () => {
    setOpen(true);
    if (self) { try { setData(await api.auraBreakdown()); } catch { setData(null); } }
  };

  return (
    <>
      <button onClick={openPanel} data-testid="aura-badge" className="text-left">
        <div className={cn("font-bold text-white", size === "lg" ? "text-base" : "text-sm")}>
          <span className="aura-text">{auraFmt(total)}</span> <span className="font-medium text-am-text2">Aura</span>
        </div>
        {(today > 0 || streak > 0) && (
          <div className="mt-0.5 flex items-center gap-2 text-[11px]">
            {today > 0 && <span className="flex items-center gap-0.5 text-aura-mint"><TrendingUp className="h-3 w-3" /> {auraFmt(today)} today</span>}
            {streak > 0 && <span className="flex items-center gap-0.5 text-am-text2"><Flame className="h-3 w-3 text-aura-gold" /> {streak}d · {multX(mult)}</span>}
          </div>
        )}
      </button>

      <Modal open={open} onClose={() => setOpen(false)} title="Aura Breakdown" testid="aura-breakdown">
        <div className="p-5">
          <div className="text-center">
            <div className="font-display text-4xl font-extrabold aura-text">{auraFmt(total)}</div>
            <div className="text-xs text-am-text2">{auraExact(total)} Aura</div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-xl border border-am-border bg-am-surface2 p-2.5"><div className="text-xs text-am-text3">Today</div><div className="text-sm font-bold text-aura-mint">+{auraFmt(today)}</div></div>
            <div className="rounded-xl border border-am-border bg-am-surface2 p-2.5"><div className="text-xs text-am-text3">Streak</div><div className="flex items-center justify-center gap-1 text-sm font-bold text-white"><Flame className="h-3.5 w-3.5 text-aura-gold" /> {streak}d</div></div>
            <div className="rounded-xl border border-am-border bg-am-surface2 p-2.5"><div className="text-xs text-am-text3">Multiplier</div><div className="text-sm font-bold text-white">{multX(mult)}</div></div>
          </div>
          {self && (
            <div className="mt-4">
              <div className="mb-2 text-xs font-semibold uppercase text-am-text3">Breakdown</div>
              {data === null ? <div className="flex justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-am-text3" /></div>
                : (data.breakdown || []).filter((b) => b.centi !== 0).length === 0 ? <p className="py-2 text-center text-sm text-am-text3">No Aura earned yet. Post, and get likes, comments, reposts and followers.</p>
                : <div className="space-y-1.5">
                    {data.breakdown.filter((b) => b.centi !== 0).map((b) => (
                      <div key={b.category} className="flex items-center justify-between rounded-lg px-1 py-1 text-sm"><span className="text-am-text2">{b.category}</span><span className="font-semibold text-white">{auraFmt(b.centi)}</span></div>
                    ))}
                  </div>}
            </div>
          )}
          <p className="mt-4 text-center text-[11px] leading-relaxed text-am-text3">Aura is AuraMax's social reputation score — earned from real engagement. It's not money, not $AURAMAX, and can't be bought.</p>
        </div>
      </Modal>
    </>
  );
}
